import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from 'app/environments/environment';
import { combineLatest, Observable, ReplaySubject, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Navigation, NavigationCounts } from './navigation.types';

const EVENT_SLUG_MAP: Record<string, string> = {
  'event-open': 'OPEN',
  'event-received': 'RECEIVED',
  'event-in-progress': 'IN_PROGRESS',
  'event-budgeted': 'BUDGETED',
  'event-approved': 'APPROVED',
  'event-completed': 'COMPLETED',
  'event-rejected': 'REJECTED',
  'event-delivered': 'DELIVERED',
};

const SCHEDULE_SLUG_MAP: Record<string, string> = {
  'schedule-open': 'OPEN',
  'schedule-assigned': 'ASSIGNED',
  'schedule-confirmed': 'CONFIRMED',
  'schedule-completed': 'COMPLETED',
  'schedule-cancelled': 'CANCELLED',
};

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _httpClient = inject(HttpClient);
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  private _counts: ReplaySubject<NavigationCounts> = new ReplaySubject<NavigationCounts>(1);

  get navigation$(): Observable<Navigation> {
    return combineLatest([this._navigation, this._counts]).pipe(
      map(([nav, counts]) => this._applyCountsToNavigation(nav, counts))
    );
  }

  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>(`${environment.apiURL}common/navigation`).pipe(
      tap(nav => this._navigation.next(nav))
    );
  }

  getCounts(): Observable<NavigationCounts> {
    return this._httpClient
      .get<NavigationCounts>(`${environment.apiURL}common/navigation/counts`)
      .pipe(tap(counts => this._counts.next(counts)));
  }

  refreshCounts(): void {
    this.getCounts().subscribe();
  }

  private _applyCountsToNavigation(nav: Navigation, counts: NavigationCounts): Navigation {
    const applyToItems = (items: FuseNavigationItem[]): FuseNavigationItem[] =>
      items.map(item => {
        const updated: FuseNavigationItem = { ...item };

        if (item.id) {
          const eventStatus = EVENT_SLUG_MAP[item.id];
          const scheduleStatus = SCHEDULE_SLUG_MAP[item.id];

          if (eventStatus !== undefined) {
            const count = counts.events[eventStatus] ?? 0;
            updated.badge = count > 0 ? { title: count.toString() } : undefined;
          } else if (scheduleStatus !== undefined) {
            const count = counts.schedules[scheduleStatus] ?? 0;
            updated.badge = count > 0 ? { title: count.toString() } : undefined;
          }
        }

        if (item.children?.length) {
          updated.children = applyToItems(item.children);
        }

        return updated;
      });

    return {
      compact: applyToItems(nav.compact ?? []),
      default: applyToItems(nav.default ?? []),
      futuristic: applyToItems(nav.futuristic ?? []),
      horizontal: applyToItems(nav.horizontal ?? []),
    };
  }
}
